import { getAllPublicCollectionItems } from "~/lib/payload/collection";
import type { Form, Template } from "~/types";
import { getStateNameFromValue } from "~/util/address";
import { createImageUrl } from "~/util/urls";

interface Args {
  templateSlug: string;
  stateSlug?: string;
}

interface TemplateData extends Template {
  form: Form;
}

interface Condition {
  "templateGroup.slug"?: {
    equals: string;
  };
  states?: {
    in: string;
  };
}

interface Metadata {
  title?: string;
  description?: string;
  image?: string;
}

interface FormData {
  id: string;
  templateId: string;
  templateGroupId: string;
  free?: boolean;
  [key: string]: unknown;
}

interface ReturnData {
  formData: FormData | false;
  metadata: Metadata;
}

const getTemplateAndFormData = async ({
  templateSlug,
  stateSlug,
}: Args): Promise<ReturnData> => {
  const and: Condition[] = [
    {
      "templateGroup.slug": {
        equals: templateSlug,
      },
    },
  ];

  let stateName = "";

  if (stateSlug === "general") {
    and.push({
      states: {
        in: "ALL",
      },
    });
  }

  // If stateSlug = 'general' then ignore and return the base level form data.
  if (stateSlug && stateSlug !== "general") {
    const stateSlugUppercase = stateSlug.toUpperCase();

    and.push({
      states: {
        in: stateSlugUppercase,
      },
    });

    stateName = getStateNameFromValue(stateSlugUppercase);
  }

  // Single API call to get template data with depth: 2
  const templateRes = await getAllPublicCollectionItems({
    collection: "templates",
    depth: 2,
    where: {
      and,
    },
  });

  // Type guard to handle both possible return types
  let templateData: TemplateData | undefined;

  if (
    "success" in templateRes &&
    templateRes.success &&
    "data" in templateRes &&
    Array.isArray(templateRes.data)
  ) {
    templateData = templateRes.data[0];
  } else if ("data" in templateRes && Array.isArray(templateRes.data)) {
    templateData = templateRes.data[0] as TemplateData;
  }

  if (!templateData?.id) {
    return {
      formData: false,
      metadata: {},
    };
  }

  // Extract metadata from templateGroup
  const templateGroup = templateData.templateGroup;

  let metadata: Metadata = {};

  if (templateGroup && typeof templateGroup !== "string" && templateGroup.id) {
    let name = templateGroup.name;

    if (stateName) {
      name = `${stateName} ${name}`;
    }

    const thumbnailUrl =
      templateGroup.thumbnail &&
      typeof templateGroup.thumbnail !== "string" &&
      templateGroup.thumbnail.url
        ? createImageUrl(templateGroup.thumbnail.url)
        : null;

    metadata = {
      title: name,
      description: templateGroup.description,
      image: thumbnailUrl || undefined,
    };
  }

  const formData = templateData?.form;

  if (!formData?.id) {
    return {
      formData: false,
      metadata,
    };
  }

  return {
    formData: {
      ...formData,
      templateId: templateData.id,
      templateGroupId:
        typeof templateData.templateGroup === "object" &&
        templateData.templateGroup !== null
          ? templateData.templateGroup.id
          : templateData.templateGroup,
      free:
        (typeof templateGroup === "object" &&
          templateGroup !== null &&
          templateGroup.free) ||
        false,
    },
    metadata,
  };
};

export default getTemplateAndFormData;
